import ConfirmDialog, { PropsType as ConfirmDialogPropsType } from '@/components/ConfirmDialog';
import FamilySelectionBox from '@/components/FamilySelectionBox';
import SelectCategoryForm from '@/components/SelectCategoryForm';
import TagsInput from '@/components/TagsInput';
import Upload from '@/components/Upload';
import Constants from '@/Constants';
import Category from '@/models/Category';
import { EntityType } from '@/models/EntityTypes';
import { LanguageType } from '@/models/LanguageType';
import List from '@/models/List';
import { resetNavigationToSetScreen } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getListClear, getListRequest, postListClear, putListRequest } from '@/store/list/action';
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import { formErrors, formInitialState, schema } from './constants';
import './style.scss';

const EditList: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const { list } = useSelector((state: ApplicationState) => state.lists);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const {
        data: { categories: fetchedCategories },
    } = useSelector((state: ApplicationState) => state.categories);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);
    const { success } = useSelector((state: ApplicationState) => state.lists);

    const [addCategory, setAddCategory] = useState<boolean | null>(null);
    const [formState, setFormState] = useState(formInitialState);
    const { name, author, description, locale, mainImage, category, category2, tags, families } = formState;
    const [{ nameError, authorError, tagsError }, setFormError] = useState(formErrors);
    const [language, setLanguage] = useState<LanguageType>('');
    const [formBkpState, setFormBkpState] = useState(formInitialState);
    const [bkpReady, setBkpReady] = useState<boolean>(false);
    const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState<ConfirmDialogPropsType>({} as ConfirmDialogPropsType);
    const [canPublish, setCanPublish] = useState<boolean>(false);
    const [categories, setCategories] = useState<Category[]>([]);

    const history = useHistory();
    const dispatch = useDispatch();

    useEffect(() => {
        resetNavigationToSetScreen(EntityType.LIST, list?.organizationId, identity.user.admin, history);
    }, [list, identity?.user.admin]);

    useEffect(() => {
        if (id) {
            dispatch(getListRequest(Number(id)));
        } else {
            dispatch(getListClear());
        }
    }, [id]);

    const handleOnChange = useCallback((fieldName: string, value: any) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    }, []);

    useEffect(() => {
        if (list) {
            handleOnChange('name', list.name);
            handleOnChange('author', list.author);
            handleOnChange('description', list.description);
            handleOnChange('locale', list.locale);
            handleOnChange('mainImage', list.mainImage);
            handleOnChange('category', list.categories && list.categories.length > 0 ? list.categories[0] : undefined);
            handleOnChange('category2', list.categories && list.categories.length > 1 ? list.categories[1] : undefined);
            handleOnChange('tags', list.tags && list.tags.length > 0 ? list.tags : []);
            handleOnChange('families', list.families && list.families.length > 0 ? list.families : []);
            setLanguage(list.locale);
            setBkpReady(true);
        } else {
            setFormState(formInitialState);
            setFormBkpState(formInitialState);
            setBkpReady(false);
            setSaveDisabled(true);
        }
    }, [handleOnChange, list]);

    useEffect(() => {
        if (success) {
            dispatch(postListClear());
        }
    }, [dispatch, success]);

    const handleSave = async () => {
        await handleSaveAndClose(false);
    };

    const handleSaveAndClose = async (willClose: boolean = true) => {
        setCanPublish(false);
        try {
            if (list) {
                setFormError(formErrors);

                await schema.validate(
                    {
                        name,
                        author,
                        locale,
                        mainImage,
                        tags,
                        categories,
                    },
                    { abortEarly: false },
                );

                const LIST: List = {
                    ...list,
                    locale: language,
                    categories,
                    tags,
                    name,
                    author,
                    description,
                    mainImage,
                    families,
                };

                dispatch(putListRequest({ list: LIST, willClose, history }));
            }
        } catch (err) {
            if (err.inner) {
                err.inner.map((error: any) =>
                    setFormError((prevState) => ({
                        ...prevState,
                        [`${error.path}Error`]: true,
                    })),
                );
            } else {
                console.error(err);
            }
        }
    };

    const navigateToAddToListScreen = () => {
        history.goBack();
    };

    useEffect(() => {
        const initialForm: boolean = _.isEqual(formBkpState, formInitialState);

        if (!initialForm && bkpReady) {
            setSaveDisabled(_.isEqual(formState, formBkpState));
        } else {
            setFormBkpState(formState);
        }

        setCanPublish(Boolean(mainImage && name && author && language && category?.id && tags.length > 0));
    }, [formState]);

    useEffect(() => {
        const addingAnotherLanguage: boolean = language !== (list && list.locale);
        if (bkpReady) {
            setFormBkpState(formInitialState);
            if (addingAnotherLanguage) {
                setSaveDisabled(false);
            } else {
                setSaveDisabled(true);
            }
        }
    }, [language]);

    const onBackClickHandler = (e: SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setModalConfig({
            leftButtonTitle: strings.CONFIRM_EXIT,
            leftClick: navigateToAddToListScreen,
            rightButtonTitle: strings.NO,
            rightClick: () => setShowModal(false),
            icon: Constants.icons.ANNOUNCEMENT,
            content: strings.LEAVE_SECTION_MESSAGE,
        } as ConfirmDialogPropsType);

        setShowModal(true);
    };

    useEffect(() => {
        const updatedCategories: Category[] = [];

        if (category?.id) {
            updatedCategories.push(category);
        }

        if (category2?.id) {
            updatedCategories.push(category2);
        }

        setCategories(updatedCategories);
    }, [category, category2]);

    return (
        <>
            <ConfirmDialog
                content={strings.LEAVE_SECTION_MESSAGE}
                icon={Constants.icons.ANNOUNCEMENT}
                leftButtonTitle={modalConfig.leftButtonTitle}
                leftClick={() => modalConfig.leftClick && modalConfig.leftClick()}
                rightButtonTitle={modalConfig.rightButtonTitle}
                rightClick={() => modalConfig.rightClick && modalConfig.rightClick()}
                show={showModal}
            />

            <div className="create-list">
                <div className="back-button go-back navbar navbar-expand sub-nav">
                    <div>
                        <a href="#" onClick={(e: SyntheticEvent) => onBackClickHandler(e)}>
                            <i className="material-icons-outlined align-middle">{Constants.icons.ARROW_LEFT}</i>
                            <span>{strings.GO_BACK}</span>
                        </a>
                    </div>
                </div>

                <div className="title-up">
                    <div className="title">
                        {id && <h1>{strings.LIST_EDIT}</h1>}
                        {!id && <h1>{`${strings.NEW_2} ${strings.LIST}`}</h1>}
                    </div>
                </div>

                <div className="content body-content">
                    <div className="ml-5 mr-5 mt-4">
                        <FamilySelectionBox disabled familyId={families[0]?.id} handleOnChange={handleOnChange} />
                    </div>

                    <div className="input-section">
                        <h1>
                            {strings.HEADER}
                            <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        <div className="ml-5 mr-5">
                            <Upload
                                onComplete={(e) => handleOnChange('mainImage', e.key)}
                                onRemove={() => handleOnChange('mainImage', undefined)}
                                previewFile={{ uri: mainImage, name: mainImage }}
                                typeAccepted="IMAGE"
                            />
                        </div>
                    </div>

                    <div className="row input-section">
                        <div className="title-container">
                            <h1>
                                {strings.TITLE}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            {nameError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                        </div>
                        <input
                            className={`form-control ${nameError ? 'error' : ''}`}
                            id="titleInput"
                            onChange={(e) => handleOnChange('name', e.target.value)}
                            placeholder={strings.TYPE_TITLE}
                            required
                            type="text"
                            value={name || ''}
                        />
                    </div>

                    <div className="row input-section">
                        <div className="title-container">
                            <h1>
                                {strings.AUTHOR}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            {authorError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                        </div>
                        <input
                            className={`form-control ${authorError ? 'error' : ''}`}
                            id="titleInput"
                            onChange={(e) => handleOnChange('author', e.target.value)}
                            placeholder={strings.TYPE_AUTHOR}
                            required
                            type="text"
                            value={author || ''}
                        />
                    </div>

                    <div className="row input-section">
                        <div className="title-container">
                            <h1>{strings.ABOUT}</h1>
                        </div>
                        <textarea
                            className="form-control"
                            id="aboutInput"
                            onChange={(e) => handleOnChange('description', e.target.value)}
                            placeholder={strings.ABOUT_INSERT}
                            rows={3}
                            value={description || ''}
                        />
                    </div>

                    <div className="list-category-section required">
                        <SelectCategoryForm
                            categoriesForm={categories}
                            familyId={families[0]?.id}
                            handleOnChange={(fieldName, categories) => {
                                const [category, category2] = categories || [];
                                handleOnChange('category', category);
                                handleOnChange('category2', category2);
                            }}
                            isColumnFormat
                        />
                    </div>

                    <div className="row input-section">
                        <div className="d-flex flex-fill flex-column">
                            <h1>
                                {strings.TAGS}
                                <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            <TagsInput hasError={tagsError} selectedTags={tags} setTags={handleOnChange} />
                        </div>
                    </div>

                    <div className="warning-input">
                        <p className="warning-text">
                            {Constants.symbol.ASTERISK}
                            {strings.REQUIRED_FIELDS}
                        </p>
                    </div>

                    <div className="d-flex vertical-center justify-content-end w-100 submit-buttons">
                        <button className="btn btn-link" onClick={(e: SyntheticEvent) => onBackClickHandler(e)} type="button">
                            {strings.CANCEL}
                        </button>
                        <button
                            className="btn btn-primary btn-sm save-close"
                            disabled={saveDisabled || !canPublish}
                            onClick={() => handleSaveAndClose()}
                            type="button"
                        >
                            {strings.SAVE_AND_CLOSE}
                        </button>
                        <button className="btn btn-primary btn-sm" disabled={saveDisabled || !canPublish} onClick={handleSave} type="button">
                            {strings.SAVE}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditList;
